import React, { useEffect, useMemo, useState } from 'react'
import { Modal } from 'react-bootstrap'
import { Button, InputField } from '@components/common'
import { IFaxFolder } from '@dataTypes/fax.type'

interface MoveFaxesModalProps {
  show: boolean
  onHide: () => void
  folders: IFaxFolder[]
  onMove: (folderId: string) => void
}

const MoveFaxesModal = ({
  show,
  onHide,
  folders,
  onMove,
}: MoveFaxesModalProps): React.ReactElement => {
  const [search, setSearch] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)

  useEffect(() => {
    if (!show) {
      setSearch('')
      setSelectedId(null)
    }
  }, [show])

  const filteredFolders = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) return folders
    return folders.filter((folder) =>
      (folder.folder_name ?? '').toLowerCase().includes(query),
    )
  }, [folders, search])

  const handleMove = (folderId: string): void => {
    if (!folderId) return
    onMove(folderId)
    onHide()
  }

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      dialogClassName="fax-move-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>Move faxes</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <InputField
          label="Search folder"
          placeholder="Search folder name here"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="fmm-folder-list">
          {filteredFolders.length > 0 ? (
            filteredFolders.map((folder) => {
              const folderId = folder.fax_folder_id ?? ''
              const isSelected = selectedId === folderId
              return (
                <div
                  key={folderId}
                  role="button"
                  tabIndex={0}
                  aria-label={folder.folder_name}
                  className={`fmm-folder-item${
                    isSelected ? ' fmm-folder-item-selected' : ''
                  }`}
                  onClick={() => setSelectedId(folderId)}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter' && e.key !== ' ') return
                    if (e.target !== e.currentTarget) return
                    e.preventDefault()
                    setSelectedId(folderId)
                  }}
                >
                  <span className="fmm-folder-info">
                    <span className="fmm-folder-icon mgc_folder_fill" />
                    <span className="fmm-folder-name">
                      {folder.folder_name}
                    </span>
                  </span>
                  {isSelected && (
                    <Button
                      type="button"
                      className="button-primary fmm-move-here"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleMove(folderId)
                      }}
                    >
                      Move here
                    </Button>
                  )}
                </div>
              )
            })
          ) : (
            <div className="fmm-folder-empty">No folders found</div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer />
    </Modal>
  )
}

export default MoveFaxesModal
